import createClient, { type Client, type Middleware } from "openapi-fetch";
import type { components, paths } from "./schema";

export type { components, paths };
export type ApiPaths = paths;
export type ApiSchemas = components["schemas"];
export type ApiClient = Client<paths>;

export interface CreateApiClientOptions {
  /** e.g. `http://localhost:8000` (no trailing slash needed). */
  baseUrl: string;
  /** Returns a bearer token (Entra access token, GoTrue JWT, license JWS, or `test:<role>`). */
  getToken?: () => Promise<string | null | undefined> | string | null | undefined;
  /** Injectable fetch for SSR / tests. */
  fetch?: typeof globalThis.fetch;
  /** Extra headers merged into every request. */
  headers?: Record<string, string>;
}

/** Shape of a FastAPI error body (`{detail}` or our Problem model). */
export interface ApiErrorBody {
  detail?: string | { msg: string; loc?: (string | number)[] }[];
  code?: string;
  status?: number;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly body: ApiErrorBody | undefined;
  readonly url: string;

  constructor(status: number, url: string, body?: ApiErrorBody, message?: string) {
    super(message ?? ApiError.messageFor(status, body));
    this.name = "ApiError";
    this.status = status;
    this.url = url;
    this.body = body;
    this.code = body?.code;
  }

  static messageFor(status: number, body?: ApiErrorBody): string {
    if (typeof body?.detail === "string") return body.detail;
    if (Array.isArray(body?.detail)) return body.detail.map((d) => d.msg).join("; ");
    return `Request failed with status ${status}`;
  }

  /** Normalise anything thrown by a request into an ApiError. */
  static from(err: unknown, url = ""): ApiError {
    if (err instanceof ApiError) return err;
    if (err instanceof Error) return new ApiError(0, url, undefined, err.message);
    return new ApiError(0, url, undefined, String(err));
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

/** Throws an ApiError when an openapi-fetch result carries an error. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) {
    throw new ApiError(
      result.response.status,
      result.response.url,
      (result.error ?? undefined) as ApiErrorBody | undefined,
    );
  }
  return result.data as T;
}

export function createAuthMiddleware(
  getToken: CreateApiClientOptions["getToken"],
  headers?: Record<string, string>,
): Middleware {
  return {
    async onRequest({ request }) {
      if (headers) for (const [k, v] of Object.entries(headers)) request.headers.set(k, v);
      const token = await getToken?.();
      if (token && !request.headers.has("authorization")) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }
      return request;
    },
  };
}

/**
 * Typed client over the committed OpenAPI schema.
 *
 * ```ts
 * const api = createApiClient({ baseUrl, getToken: acquireToken });
 * const { data } = await api.GET("/healthz");
 * ```
 */
export function createApiClient(options: CreateApiClientOptions): ApiClient {
  const client = createClient<paths>({
    baseUrl: options.baseUrl.replace(/\/+$/, ""),
    // Resolve fetch lazily so test interceptors (MSW) and SSR polyfills installed after
    // construction are honoured.
    fetch: options.fetch ?? ((input: Request) => globalThis.fetch(input)),
  });
  client.use(createAuthMiddleware(options.getToken, options.headers));
  return client;
}

/**
 * Escape hatch for endpoints that are not in `openapi.json` yet (M0 placeholder spec).
 * Delete call sites as the API team regenerates the schema.
 */
export async function rawRequest<T>(
  options: CreateApiClientOptions,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const f = options.fetch ?? globalThis.fetch;
  const url = `${options.baseUrl.replace(/\/+$/, "")}${path}`;
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  for (const [k, v] of Object.entries(options.headers ?? {})) headers.set(k, v);
  const token = await options.getToken?.();
  if (token && !headers.has("authorization")) headers.set("Authorization", `Bearer ${token}`);
  let res: Response;
  try {
    res = await f(url, { ...init, headers });
  } catch (err) {
    throw ApiError.from(err, url);
  }
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) throw new ApiError(res.status, url, body as ApiErrorBody);
  return body as T;
}
